import { countries } from "@redotech/locale/countries";
import { shopifyProvinces } from "@redotech/locale/provinces";
import { RedoMerchantRpcClient } from "@redotech/merchant-sdk-client/client";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useSearch } from "@redotech/react-util/search";
import { TagKind } from "@redotech/redo-common-model-tags-common/tag";
import { omsConditionStepSchema } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import {
  BaseWhereStep,
  DEFAULT_FORECAST_DELIVERY_DAYS,
  getConditionTypeLabel,
  omsConditionToWhereConditionTypes,
  OmsCustomString1ConditionStep,
  OmsDeliveryMethodConditionStep,
  OmsDestinationForecastTemperatureConditionStep,
  OmsLineItemCustomAttributeConditionStep,
  OmsMarketplaceConditionStep,
  OmsMarketplaceInternalNotesConditionStep,
  OmsOrderNotesConditionStep,
  OmsPackageIsLearnedConditionStep,
  OmsProductCustomString1ConditionStep,
  OmsProductWhereCondition,
} from "@redotech/redo-flows-common/oms/conditions";
import { OmsConditionStep } from "@redotech/redo-flows-common/steps";
import { StringToListMatchType } from "@redotech/redo-flows-core-common/condition";
import { OmsConditionType } from "@redotech/redo-flows-core-common/oms-condition-type";
import { Schema } from "@redotech/redo-flows-core-schema-common/type-system/schema";
import { FulfillmentGroupListMatchValuesFilterName } from "@redotech/redo-fulfillment-common/fulfillment-group-summary-input";
import {
  RedoShopifyDeliveryMethodTypeToString as RedoShopifyDeliveryMethodLabels,
  RedoShopifyDeliveryMethodType,
} from "@redotech/redo-fulfillment-group-common/fulfillment-delivery-method-type";
import {
  riskLabels,
  ShopifyRiskAssessmentResult,
} from "@redotech/redo-fulfillment-group-common/shopify-fulfillment-schemas";
import { t, useTranslation } from "@redotech/redo-i18n";
import FilterIcon from "@redotech/redo-icons/filter-lines_filled.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { RedoOutboundLabelsRpcClientContext } from "@redotech/redo-merchant-fulfillment-app/outbound-labels-rpc-client";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc-client/client";
import { Provider } from "@redotech/redo-order-common/provider";
import { orderProviderToLabel } from "@redotech/redo-order-core-common/order";
import { Team } from "@redotech/redo-team-common/team";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import {
  RedoListItem,
  TextRedoListItem,
} from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import Fuse from "fuse.js";
import * as React from "react";
import { Fragment, memo, useContext, useMemo, useState } from "react";
import { DeepPartial, FrontendStepType, PartialWithRequiredType } from "..";
import { getConditionIntroText } from "../../nodes/oms/oms-condition-node";
import { SingleNumberStepInput } from "../common/single-number-step-input";
import { OmsDateMatchTypeSelect } from "./date-match-type-select";
import { OmsDatePickerInput } from "./date-picker-input";
import { ListMatchFixedValuesInput } from "./list-match-fixed-values-input";
import { OmsNumberMatchTypeSelect } from "./number-match-type-select";
import { OmsListMatchValuesInput } from "./oms-list-match-values-input";
import { OmsListToListMatchTypeSelect } from "./oms-list-to-list-match-type-select";
import { OmsRequireExactMatchInput } from "./oms-require-exact-match-input";
import { OmsSingleNumberStepInput } from "./single-number-step-input";
import { StringMatchTypeSelect } from "./string-match-type-select";
import { StringToListMatchTypeSelect } from "./string-to-list-match-type-select";
import { TextInput } from "./text-input";
import { OmsWeightStepInput } from "./weight-step-input";

const zipCodeMatchTypes = [
  StringToListMatchType.IS_ANY_OF,
  StringToListMatchType.IS_NONE_OF,
] as const;

type CustomStringConditionType =
  | OmsConditionType.CUSTOM_STRING_1
  | OmsConditionType.CUSTOM_STRING_2
  | OmsConditionType.CUSTOM_STRING_3
  | OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1
  | OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2
  | OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3;

function createOptionsFetcher(
  filterName: FulfillmentGroupListMatchValuesFilterName,
  rpcClient: RedoOutboundLabelsRpcClient,
): ({ searchText }: { searchText: string }) => Promise<string[]> {
  return async ({ searchText }) => {
    const { values } = await rpcClient.getFulfillmentGroupUniqueValues({
      advancedFiltersData: [],
      filterName,
      filterSearch: searchText,
    });
    return values;
  };
}

/**
 * The unique-values aggregation only sees tags the marketplace last synced onto
 * a group, so internal Redo tags are never in it. This RPC unions both.
 */
function createCustomerTagOptionsFetcher(
  merchantRpcClient: RedoMerchantRpcClient,
): ({ searchText }: { searchText: string }) => Promise<string[]> {
  return async ({ searchText }) => {
    const { tags } = await merchantRpcClient.searchCustomerTags({
      searchString: searchText,
    });
    return tags;
  };
}

// search-tag:ADDING_AN_OMS_CONDITION
export const OmsConditionInputs = memo(function OmsConditionInputs({
  schema,
  step,
  setStep,
  onClose,
  fieldPicker,
}: {
  step: PartialWithRequiredType<OmsConditionStep>;
  setStep: (step: PartialWithRequiredType<OmsConditionStep>) => void;
  schema: Schema;
  onClose?: () => void;
  fieldPicker?: React.ReactNode;
}) {
  const { t } = useTranslation();
  const setConditionType = useHandler(
    (item: RedoListItem<OmsConditionType>) => {
      setStep({
        type: step.type,
        nextTrueId: step.nextTrueId,
        nextFalseId: step.nextFalseId,
        id: step.id,
        conditionType: item.value,
      } as PartialWithRequiredType<OmsConditionStep>);
    },
  );

  const setStepFields = useHandler((newStep: DeepPartial<OmsConditionStep>) => {
    setStep({
      ...newStep,
      type: step.type,
      nextTrueId: step.nextTrueId,
      nextFalseId: step.nextFalseId,
      id: step.id,
    });
  });

  const showFilterButton = useMemo(() => {
    if (!step.conditionType) {
      return false;
    }
    return omsConditionToWhereConditionTypes[step.conditionType].length > 0;
  }, [step]);

  const onClickFilter = useHandler(() => {
    const where = step.where ?? [];
    where.push({});
    setStepFields({
      ...step,
      where: where as DeepPartial<OmsProductWhereCondition>[],
    });
  });

  const introText = getConditionIntroText(step);

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="sm" fontWeight="semibold">
          {t("Condition")}
        </Text>
        {onClose && (
          <div className={inspectorStyles.closeButton} onClick={onClose}>
            <XIcon height="16" width="16" />
          </div>
        )}
      </Flex>
      <Flex dir="column" gap="none">
        <Flex align="center" justify="space-between" w="full">
          <Text fontSize="xs" fontWeight="medium">
            {introText}
          </Text>
          {showFilterButton && (
            <RedoButton
              aria-label={t("Filter")}
              IconLeading={() => <FilterIcon />}
              onClick={onClickFilter}
            />
          )}
        </Flex>
        <Flex align="center" dir="row" gap="md" w="full" wrap="wrap">
          <ConditionFields
            fieldPicker={fieldPicker}
            setConditionType={setConditionType}
            setStep={setStepFields}
            step={step}
          />
        </Flex>
      </Flex>
      <WhereFilters
        clauses={step.where ?? []}
        setStep={setStepFields}
        step={step}
      />
    </Flex>
  );
});

const ConditionFields = memo(function ConditionFields({
  conditionTypeOptions,
  fieldPicker,
  step,
  setStep,
  setConditionType,
  index = 0,
}: {
  conditionTypeOptions?: OmsConditionType[];
  fieldPicker?: React.ReactNode;
  step: DeepPartial<OmsConditionStep>;
  setStep: (step: DeepPartial<OmsConditionStep>, index: number) => void;
  setConditionType: (
    item: RedoListItem<OmsConditionType>,
    index: number,
  ) => void;
  index?: number;
}) {
  const { t } = useTranslation();
  const team = useContext(TeamContext);
  const outboundLabelRpcClient = useRequiredContext(
    RedoOutboundLabelsRpcClientContext,
  );

  const merchantRpcClient = useRequiredContext(RedoMerchantRpcClientContext);

  const [searchString, setSearchString] = useState("");

  const setStepFields = useHandler((newStep: DeepPartial<OmsConditionStep>) => {
    setStep(newStep, index);
  });

  const { conditionFields } = getConditionFields(
    step,
    setStepFields,
    outboundLabelRpcClient,
    merchantRpcClient,
    team,
  );

  const optionSelected = useHandler(
    ({ item }: { item: RedoListItem<OmsConditionType> }) => {
      setConditionType(item, index);
    },
  );

  const allOptions = useMemo(
    () => getOmsConditionTypeOptions(conditionTypeOptions, team),
    [conditionTypeOptions, team],
  );

  const conditionSearcher = useMemo(() => {
    return new Fuse<RedoListItem<OmsConditionType>>([], {
      keys: ["text"],
      threshold: 0.3,
    });
  }, []);

  const filteredOptions = useSearch(
    conditionSearcher,
    allOptions,
    searchString,
  );

  return (
    <>
      {fieldPicker ?? (
        <RedoSingleSelectDropdownInput<OmsConditionType>
          fitToAnchor={false}
          flexGrow={false}
          options={filteredOptions}
          optionSelected={optionSelected}
          placeholder={t("Select a field")}
          searchPlaceholder={t("Search conditions...")}
          searchString={searchString}
          selectedItem={allOptions.find((o) => o.id === step.conditionType)}
          setSearchString={setSearchString}
          size="xs"
        />
      )}
      {conditionFields.map((field, index) => (
        <Fragment key={index}>{field}</Fragment>
      ))}
    </>
  );
});

const WhereFilters = memo(function WhereFilters({
  clauses,
  step,
  setStep,
}: {
  clauses: DeepPartial<BaseWhereStep>[];
  step: PartialWithRequiredType<OmsConditionStep>;
  setStep: (step: PartialWithRequiredType<OmsConditionStep>) => void;
}) {
  const { t } = useTranslation();
  const setSubStep = useHandler(
    (newStep: DeepPartial<OmsConditionStep>, index: number) => {
      if (step.where === undefined) {
        step.where = [];
      }
      if (index > step.where.length) {
        index = step.where.length;
      }
      step.where[index] = newStep;
      setStep(step);
    },
  );

  const setConditionType = useHandler(
    (item: RedoListItem<OmsConditionType>, index: number) => {
      setSubStep(
        { conditionType: item.value } as DeepPartial<OmsConditionStep>,
        index,
      );
    },
  );

  const onDeleteWhereClause = useHandler((index: number) => {
    step.where?.splice(index, 1);
    setStep(step);
  });

  return (
    <Flex dir="column" justify="flex-start">
      {clauses.map((clause, index) => (
        <Flex
          dir="column"
          gap="none"
          justify="flex-start"
          key={`where-${index}`}
        >
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" fontWeight="medium">
              {index === 0 ? t("Where") : t("And")}
            </Text>
            <RedoButton
              aria-label={t("Remove condition")}
              IconLeading={() => <XIcon />}
              onClick={() => onDeleteWhereClause(index)}
            />
          </Flex>
          <Flex align="center" dir="row" gap="md" w="full" wrap="wrap">
            <ConditionFields
              conditionTypeOptions={
                step.conditionType
                  ? omsConditionToWhereConditionTypes[step.conditionType]
                  : undefined
              }
              index={index}
              setConditionType={setConditionType}
              setStep={setSubStep}
              step={clause as DeepPartial<OmsConditionStep>}
            />
          </Flex>
        </Flex>
      ))}
    </Flex>
  );
});

function getConditionFields(
  step: DeepPartial<OmsConditionStep>,
  setStep: (step: DeepPartial<OmsConditionStep>) => void,
  outboundLabelRpcClient: RedoOutboundLabelsRpcClient,
  merchantRpcClient: RedoMerchantRpcClient,
  team?: Team,
) {
  let conditionFields: React.ReactNode[] = [];
  let showFilterButton = false;

  switch (step.conditionType) {
    case OmsConditionType.FRAUD_RISK:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural={false}
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="fraud-risk-matcher"
          optionsFetcher={() =>
            Promise.resolve(
              Object.values(ShopifyRiskAssessmentResult).map(
                (value) => riskLabels[value],
              ),
            )
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Select risk types...")}
        />,
      ];
      break;
    case OmsConditionType.AVAILABLE_INVENTORY:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.ORDER_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="order-tags-matcher"
          optionsFetcher={createOptionsFetcher(
            "shopifyOrderTags",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search tags...")}
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.SELECTED_SHIPPING:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural={false}
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="shipping-matcher"
          optionsFetcher={createOptionsFetcher(
            "requestedService",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search shipping lines...")}
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.SKU:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="sku-matcher"
          optionsFetcher={createOptionsFetcher(
            "itemSkus",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search product SKUs...")}
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.PRODUCT_NAME:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="product-name-matcher"
          optionsFetcher={createOptionsFetcher(
            "productTitle",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search product names...")}
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.VARIANT_TITLE:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="variant-title-matcher"
          optionsFetcher={createOptionsFetcher(
            "productVariant",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search variant titles...")}
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.PRODUCT_TYPE:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="product-type-matcher"
          optionsFetcher={createOptionsFetcher(
            "productType",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search product types...")}
        />,
        <OmsRequireExactMatchInput key="3" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.ITEM_QUANTITY:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.PACKAGE_WEIGHT_OZ:
      conditionFields = [
        <OmsNumberMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsWeightStepInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.TOTAL_VALUE:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.TAXES_PAID:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.DESTINATION_COUNTRY:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="country-name-matcher"
          optionsFetcher={() =>
            Promise.resolve(countries.map((country) => country.name))
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search countries...")}
        />,
      ];
      break;
    case OmsConditionType.DESTINATION_PROVINCE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="province-name-matcher"
          optionsFetcher={({ searchText }) =>
            Promise.resolve(
              shopifyProvinces
                .filter((province) =>
                  province.province
                    .toLowerCase()
                    .includes(searchText.toLowerCase()),
                )
                .sort((p1, p2) => p1.province.localeCompare(p2.province))
                .map((province) => province.province)
                .slice(0, 100),
            )
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search provinces...")}
        />,
        <Flex key="province-abbreviation-hint" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("State names and abbreviations are treated as equivalent.")}
          </Text>
        </Flex>,
      ];
      break;
    case OmsConditionType.DELIVERY_METHOD:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <ListMatchFixedValuesInput
          key="delivery-method-matcher"
          label={t("Select delivery method(s)")}
          options={Object.entries(RedoShopifyDeliveryMethodType).map(
            ([id, value]) => ({
              id,
              value,
              label: RedoShopifyDeliveryMethodLabels[value],
            }),
          )}
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsDeliveryMethodConditionStep>}
        />,
      ];
      break;
    case OmsConditionType.STREET_ADDRESS:
      // Street address uses a different input UI with StringMatchOperator
      conditionFields = [
        <StringMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <TextInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.STREET_ADDRESS_LIST_INPUT:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="street-address-list-input-matcher"
          optionsFetcher={() => {
            return Promise.resolve([]);
          }}
          setStep={setStep}
          step={step}
          tagInputPlaceholder=""
        />,
      ];
      break;
    case OmsConditionType.ZIP_CODE:
      conditionFields = [
        <StringToListMatchTypeSelect
          key="1"
          matchTypes={zipCodeMatchTypes}
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="zip-code-matcher"
          optionsFetcher={() => Promise.resolve([])}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Enter zip codes...")}
        />,
        <Flex key="zip-code-hint" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("A 5-digit zip code also matches its ZIP+4 (e.g. 90210-1234).")}
          </Text>
        </Flex>,
      ];
      break;
    case OmsConditionType.SALES_CHANNEL:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="sales-channel-matcher"
          optionsFetcher={createOptionsFetcher(
            "stores",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search sales channels...")}
        />,
      ];
      break;
    case OmsConditionType.PRODUCT_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="product-tag-matcher"
          optionsFetcher={createOptionsFetcher(
            "shopifyProductTags",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search product tags...")}
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.FULFILLMENT_LOCATION:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="location-matcher"
          optionsFetcher={async () => {
            try {
              // Include locations from teams this 3PL fulfills for
              const { locations } =
                await merchantRpcClient.getLocationsWithFulfillsFor({});
              return locations.map((location) => location.name);
            } catch (error) {
              console.error(error);
              return [];
            }
          }}
          preserveCommas
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search locations...")}
        />,
      ];
      break;
    case OmsConditionType.ORIGIN_STORE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="origin-store-matcher"
          optionsFetcher={createOptionsFetcher(
            "originStore",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search origin stores...")}
        />,
      ];
      break;
    case OmsConditionType.ORDER_NOTES:
      conditionFields = [
        <StringMatchTypeSelect
          key="1"
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsOrderNotesConditionStep>}
        />,
        <TextInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.MARKETPLACE_INTERNAL_NOTES:
      conditionFields = [
        <StringMatchTypeSelect
          key="1"
          setStep={setStep}
          step={
            step as PartialWithRequiredType<OmsMarketplaceInternalNotesConditionStep>
          }
        />,
        <TextInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.MARKETPLACE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <ListMatchFixedValuesInput
          key="marketplace-matcher"
          label={t("Select marketplace(s)")}
          options={Object.values(Provider).map((value) => ({
            id: value,
            value,
            label: orderProviderToLabel[value],
          }))}
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsMarketplaceConditionStep>}
        />,
      ];
      break;
    case OmsConditionType.SHIPMENT_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="shipment-tag-matcher"
          optionsFetcher={async () => {
            try {
              const { tags } = await merchantRpcClient.getTeamTags({
                kind: TagKind.FULFILLMENT_GROUP,
              });
              return tags.map((tag) => tag.name);
            } catch (error) {
              console.error(error);
              return [];
            }
          }}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search fulfillment tags...")}
        />,
        <OmsRequireExactMatchInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.CUSTOM_STRING_1:
    case OmsConditionType.CUSTOM_STRING_2:
    case OmsConditionType.CUSTOM_STRING_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3: {
      const conditionTypeToFilterName: Record<
        CustomStringConditionType,
        FulfillmentGroupListMatchValuesFilterName
      > = {
        [OmsConditionType.CUSTOM_STRING_1]: "customString1",
        [OmsConditionType.CUSTOM_STRING_2]: "customString2",
        [OmsConditionType.CUSTOM_STRING_3]: "customString3",
        [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1]: "customAttributeString1",
        [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2]: "customAttributeString2",
        [OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3]: "customAttributeString3",
      };

      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          addTagText={t("Add value")}
          key={`custom-string-${step.conditionType}-matcher`}
          optionsFetcher={createOptionsFetcher(
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            conditionTypeToFilterName[step.conditionType!],
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step as PartialWithRequiredType<OmsCustomString1ConditionStep>}
          tagInputPlaceholder={t("Select {{fieldName}} values...", {
            fieldName: getConditionTypeLabel(step.conditionType, team),
          })}
        />,
      ];
      break;
    }
    case OmsConditionType.CUSTOM_NUMBER_1:
    case OmsConditionType.CUSTOM_NUMBER_2:
    case OmsConditionType.CUSTOM_NUMBER_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_1:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_2:
    case OmsConditionType.PRODUCT_CUSTOM_NUMBER_3:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.CUSTOM_DATE_1:
    case OmsConditionType.CUSTOM_DATE_2:
    case OmsConditionType.CUSTOM_DATE_3:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2:
    case OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_1:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_2:
    case OmsConditionType.PRODUCT_CUSTOM_DATE_3:
      conditionFields = [
        <OmsDateMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsDatePickerInput key="1" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.PRODUCT_CUSTOM_STRING_1:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_2:
    case OmsConditionType.PRODUCT_CUSTOM_STRING_3:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          addTagText={t("Add value")}
          key={`product-custom-string-${step.conditionType}-matcher`}
          // Product metafield values are free-form per product; there's no
          // server-side unique-value index for them, so no autocomplete.
          optionsFetcher={() => Promise.resolve([])}
          setStep={setStep}
          step={
            step as PartialWithRequiredType<OmsProductCustomString1ConditionStep>
          }
          tagInputPlaceholder={t("Select {{fieldName}} values...", {
            fieldName: getConditionTypeLabel(step.conditionType, team),
          })}
        />,
      ];
      break;
    case OmsConditionType.CUSTOMER_NAME:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="customer-name-matcher"
          optionsFetcher={createOptionsFetcher(
            "customer",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search customer names...")}
        />,
      ];
      break;
    case OmsConditionType.CUSTOMER_ORDER_COUNT:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.CUSTOMER_TAG:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="customer-tag-matcher"
          optionsFetcher={createCustomerTagOptionsFetcher(merchantRpcClient)}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search tags...")}
        />,
        <OmsRequireExactMatchInput key="2" setStep={setStep} step={step} />,
      ];
      break;
    case OmsConditionType.RECOGNIZE_SEGMENT:
      // Saved legacy conditions still execute, but cannot be configured here.
      conditionFields = [];
      break;
    case OmsConditionType.ITEM_COST:
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="1" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer={false}
            money
            setStep={setStep}
            step={step}
          />
        </Flex>,
      ];
      break;
    case OmsConditionType.VENDOR:
      conditionFields = [
        <OmsListToListMatchTypeSelect
          fieldLabelIsPlural
          key="1"
          setStep={setStep}
          step={step}
        />,
        <OmsListMatchValuesInput
          key="vendor-matcher"
          optionsFetcher={createOptionsFetcher(
            "vendors",
            outboundLabelRpcClient,
          )}
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Search vendors...")}
        />,
        <OmsRequireExactMatchInput key="2" setStep={setStep} step={step} />,
      ];
      showFilterButton = true;
      break;
    case OmsConditionType.USPS_ZONE:
      conditionFields = [
        <StringToListMatchTypeSelect key="1" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          key="usps-zone-matcher"
          optionsFetcher={() =>
            Promise.resolve(
              [
                { label: "Zone 1", value: "1" },
                { label: "Zone 2", value: "2" },
                { label: "Zone 3", value: "3" },
                { label: "Zone 4", value: "4" },
                { label: "Zone 5", value: "5" },
                { label: "Zone 6", value: "6" },
                { label: "Zone 7", value: "7" },
                { label: "Zone 8", value: "8" },
              ].map((zone) => zone.value),
            )
          }
          setStep={setStep}
          step={step}
          tagInputPlaceholder={t("Select zones...")}
        />,
      ];
      break;
    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE:
      conditionFields = [
        <RedoTextInput
          key="attribute-key-input"
          placeholder={t("Attribute key")}
          setValue={(value) => setStep({ ...step, matchKey: value })}
          size="sm"
          value={
            (step as DeepPartial<OmsLineItemCustomAttributeConditionStep>)
              .matchKey || ""
          }
        />,
        <StringToListMatchTypeSelect key="2" setStep={setStep} step={step} />,
        <OmsListMatchValuesInput
          addTagText={t("Add value")}
          key="line-item-custom-attribute-matcher"
          optionsFetcher={() => Promise.resolve([])}
          setStep={setStep}
          step={
            step as PartialWithRequiredType<OmsLineItemCustomAttributeConditionStep>
          }
          tagInputPlaceholder={t("Enter attribute values...")}
        />,
      ];
      break;
    case OmsConditionType.ADDRESS_VERIFIED:
      conditionFields = [
        <RedoSingleSelectDropdownInput<boolean>
          key="address-verified-select"
          options={[
            {
              id: "verified",
              type: "text",
              text: t("Is verified"),
              value: true,
            },
            {
              id: "not-verified",
              type: "text",
              text: t("Is not verified"),
              value: false,
            },
          ]}
          optionSelected={({ item }) =>
            setStep({ ...step, matchValue: item.value })
          }
          placeholder={t("Select...")}
          selectedItem={
            step.matchValue === true
              ? {
                  id: "verified",
                  type: "text",
                  text: t("Is verified"),
                  value: true,
                }
              : step.matchValue === false
                ? {
                    id: "not-verified",
                    type: "text",
                    text: t("Is not verified"),
                    value: false,
                  }
                : undefined
          }
          size="xs"
        />,
      ];
      break;
    case OmsConditionType.PACKAGE_IS_LEARNED:
      conditionFields = [
        <RedoSingleSelectDropdownInput<boolean>
          key="package-is-learned-select"
          options={[
            { id: "yes", type: "text", text: t("Yes"), value: true },
            { id: "no", type: "text", text: t("No"), value: false },
          ]}
          optionSelected={({ item }) =>
            setStep({
              ...(step as PartialWithRequiredType<OmsPackageIsLearnedConditionStep>),
              matchValue: item.value,
            })
          }
          placeholder={t("Select...")}
          selectedItem={
            (step as DeepPartial<OmsPackageIsLearnedConditionStep>)
              .matchValue === true
              ? { id: "yes", type: "text", text: t("Yes"), value: true }
              : (step as DeepPartial<OmsPackageIsLearnedConditionStep>)
                    .matchValue === false
                ? { id: "no", type: "text", text: t("No"), value: false }
                : undefined
          }
          size="xs"
        />,
      ];
      break;
    case OmsConditionType.DESTINATION_FORECAST_TEMPERATURE: {
      const forecastStep =
        step as DeepPartial<OmsDestinationForecastTemperatureConditionStep>;
      conditionFields = [
        <Flex align="center" dir="row" gap="md" key="threshold" wrap="wrap">
          <OmsNumberMatchTypeSelect setStep={setStep} step={step} />
          <OmsSingleNumberStepInput
            integer
            money={false}
            setStep={setStep}
            step={step}
          />
          <Text fontSize="sm" textColor="secondary">
            °F
          </Text>
        </Flex>,
        <Flex align="center" dir="row" gap="md" key="default-days" wrap="wrap">
          <Text fontSize="sm" textColor="secondary">
            {t("Default delivery days (used when no delivery date is known)")}
          </Text>
          <SingleNumberStepInput
            money={false}
            setValue={(defaultDeliveryDays) =>
              setStep({
                ...(step as PartialWithRequiredType<OmsDestinationForecastTemperatureConditionStep>),
                defaultDeliveryDays,
              })
            }
            stepInteger
            value={
              forecastStep.defaultDeliveryDays ?? DEFAULT_FORECAST_DELIVERY_DAYS
            }
          />
        </Flex>,
        <Flex
          bgColor="warning-secondary"
          borderColor="fg-warning-secondary"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          gap="xs"
          key="us-only-callout"
          p="md"
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {t("US addresses only")}
          </Text>
          <Text fontSize="xs" textColor="secondary">
            {t(
              "Checks the forecast high temperature at your customer's address for the day their order should arrive (US addresses only). If we don't know the delivery day yet, we count ahead by the number of days set above — 3 by default. And if we can't get a forecast — a non-US address, a date too far out, or the weather service being briefly down — we treat that day as hot, just to be safe.",
            )}
          </Text>
        </Flex>,
      ];
      break;
    }
    case undefined:
      break;
    default:
      assertNever(step);
  }
  if (
    step.conditionType !== OmsConditionType.ZIP_CODE &&
    (step as { matchType?: string }).matchType ===
      StringToListMatchType.IS_ANY_OF
  ) {
    conditionFields.push(
      <Flex key="is-any-of-hint" w="full">
        <Text fontSize="xs" textColor="secondary">
          {t(
            "“Is any of” matches only when the value exactly equals one of these entries. Use “contains any of” to also match values that merely contain them.",
          )}
        </Text>
      </Flex>,
    );
  }
  return { conditionFields, showFilterButton };
}

export function getOmsConditionTypeOptions(
  conditionTypes?: OmsConditionType[],
  team?: Team,
): TextRedoListItem<OmsConditionType>[] {
  if (!conditionTypes) {
    // get all condition types, but filter out custom field condition types if the merchant doesn't have them configured
    conditionTypes = Object.values(OmsConditionType).filter((type) => {
      return (
        !(
          type == OmsConditionType.CUSTOM_STRING_1 &&
          !team?.settings?.outboundLabels?.customMetadata?.customString1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_STRING_2 &&
          !team?.settings?.outboundLabels?.customMetadata?.customString2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_STRING_3 &&
          !team?.settings?.outboundLabels?.customMetadata?.customString3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_DATE_1 &&
          !team?.settings?.outboundLabels?.customMetadata?.customDate1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_DATE_2 &&
          !team?.settings?.outboundLabels?.customMetadata?.customDate2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_DATE_3 &&
          !team?.settings?.outboundLabels?.customMetadata?.customDate3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_NUMBER_1 &&
          !team?.settings?.outboundLabels?.customMetadata?.customNumber1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_NUMBER_2 &&
          !team?.settings?.outboundLabels?.customMetadata?.customNumber2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_NUMBER_3 &&
          !team?.settings?.outboundLabels?.customMetadata?.customNumber3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_STRING_1 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeString1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_STRING_2 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeString2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_STRING_3 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeString3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_1 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeNumber1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_2 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeNumber2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_NUMBER_3 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeNumber3
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_DATE_1 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeDate1
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_DATE_2 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeDate2
        ) &&
        !(
          type == OmsConditionType.CUSTOM_ATTRIBUTE_DATE_3 &&
          !team?.settings?.outboundLabels?.customAttributes
            ?.customAttributeDate3
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_STRING_1 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomString1
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_STRING_2 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomString2
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_STRING_3 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomString3
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_NUMBER_1 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomNumber1
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_NUMBER_2 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomNumber2
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_NUMBER_3 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomNumber3
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_DATE_1 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomDate1
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_DATE_2 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomDate2
        ) &&
        !(
          type == OmsConditionType.PRODUCT_CUSTOM_DATE_3 &&
          !team?.settings?.outboundLabels?.productCustomMetadata
            ?.productCustomDate3
        )
      );
    });
  }
  const conditionTypeOptions: TextRedoListItem<OmsConditionType>[] =
    conditionTypes
      .filter((type) => type !== OmsConditionType.RECOGNIZE_SEGMENT)
      .map((type) => ({
        value: type,
        type: "text",
        id: type,
        text: getConditionTypeLabel(type, team),
      }));
  conditionTypeOptions.sort((a, b) => a.text.localeCompare(b.text));
  return conditionTypeOptions;
}

export const omsConditionStepType: FrontendStepType<
  PartialWithRequiredType<OmsConditionStep>
> = {
  valid: (step) => omsConditionStepSchema.safeParse(step).success,
  type: "custom",
  customInspector: OmsConditionInputs,
};
